import { useEffect, useState } from 'react';

export function CongoMapAnimation() {
  const [activeFlow, setActiveFlow] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFlow(prev => (prev + 1) % 6);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Brazzaville arrondissements and key locations
  const brazzavilleLocations = [
    { name: "Makélékélé", x: 280, y: 520, size: "large" },
    { name: "Bacongo", x: 400, y: 470, size: "large" },
    { name: "Poto-Poto", x: 250, y: 370, size: "large" },
    { name: "Moungali", x: 370, y: 290, size: "large" },
    { name: "Ouenzé", x: 200, y: 260, size: "medium" },
    { name: "Talangaï", x: 460, y: 230, size: "medium" },
    { name: "Mfilou", x: 130, y: 430, size: "medium" },
    { name: "Madibou", x: 490, y: 370, size: "medium" },
    { name: "Djiri", x: 400, y: 150, size: "small" },
  ];

  // Flow paths connecting locations
  const flowPaths = [
    { path: "M 200 260 Q 240 320 260 400 T 280 520", from: "Ouenzé", to: "Makélékélé" },
    { path: "M 460 230 Q 430 300 410 380 T 400 470", from: "Talangaï", to: "Bacongo" },
    { path: "M 400 150 Q 380 200 375 250 T 370 290", from: "Djiri", to: "Moungali" },
    { path: "M 130 430 Q 180 460 230 490 T 280 520", from: "Mfilou", to: "Makélékélé" },
    { path: "M 490 370 Q 450 400 420 440 T 400 470", from: "Madibou", to: "Bacongo" },
    { path: "M 250 370 Q 290 400 340 440 T 400 470", from: "Poto-Poto", to: "Bacongo" },
  ];

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Glow background */}
      <div className="absolute inset-0 bg-gradient-radial from-congo-green/10 via-transparent to-transparent" />
      
      <svg
        viewBox="0 0 650 750"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.3))' }}
      >
        <defs>
          {/* Glowing orange gradient for the flow */}
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="40%" stopColor="#22c55e" />
            <stop offset="50%" stopColor="#fff" />
            <stop offset="60%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
          
          {/* Filter for glow effect */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Radial gradient for location nodes */}
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="1" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Title */}
        <text x="325" y="60" fill="#22c55e" fontSize="28" fontWeight="bold" textAnchor="middle" filter="url(#glow)">
          Brazzaville
        </text>
        <text x="325" y="90" fill="#94a3b8" fontSize="14" textAnchor="middle">
          Capitale de la République du Congo
        </text>
        
        {/* Flowing light paths connecting locations */}
        {flowPaths.map((flow, index) => (
          <g key={index}>
            {/* Background path */}
            <path
              d={flow.path}
              fill="none"
              stroke="#475569"
              strokeWidth="1"
              opacity="0.3"
              strokeDasharray="4 4"
            />
            {/* Animated flowing light */}
            <path
              d={flow.path}
              fill="none"
              stroke="url(#flowGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              filter="url(#glow)"
              style={{
                strokeDasharray: '40 280',
                strokeDashoffset: activeFlow === index ? -320 : 0,
                transition: 'stroke-dashoffset 0.8s ease-in-out',
                opacity: activeFlow === index ? 1 : 0.15,
              }}
            />
          </g>
        ))}
        
        {/* Location nodes */}
        {brazzavilleLocations.map((location, index) => {
          const radius = location.size === "large" ? 16 : location.size === "medium" ? 11 : 8;
          const isActive = activeFlow === index % 6;
          
          return (
            <g key={location.name}>
              {/* Outer glow */}
              <circle
                cx={location.x}
                cy={location.y}
                r={radius + 8}
                fill="url(#nodeGlow)"
                opacity={isActive ? 0.6 : 0.2}
                className="transition-opacity duration-300"
              />
              {/* Main circle */}
              <circle
                cx={location.x}
                cy={location.y}
                r={radius}
                fill={location.size === "large" ? "#22c55e" : "#3b82f6"}
                filter="url(#glow)"
                className={isActive ? 'animate-pulse' : ''}
                style={{ transformOrigin: `${location.x}px ${location.y}px` }}
              />
              {/* Inner highlight */}
              <circle
                cx={location.x}
                cy={location.y}
                r={radius / 2}
                fill="#fff"
                opacity="0.8"
              />
              {/* Label */}
              <text
                x={location.x}
                y={location.y + radius + 16}
                fill={location.size === "large" ? "#22c55e" : "#94a3b8"}
                fontSize={location.size === "large" ? "11" : "9"}
                fontWeight={location.size === "large" ? "bold" : "normal"}
                textAnchor="middle"
              >
                {location.name}
              </text>
            </g>
          );
        })}
      </svg>
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-congo-green/40 rounded-full animate-float"
            style={{
              left: `${15 + i * 10}%`,
              top: `${20 + (i % 4) * 15}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + i * 0.3}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
